import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { ArrowBack, PlayArrow, Stop, Delete, FileCopy } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import ExperimentResultsTable from '../../components/experiments/ExperimentResultsTable';
import WindowsTimeline from '../../components/experiments/WindowsTimeline';
import ValidationGateAlert from '../../components/experiments/ValidationGateAlert';
import { experimentService } from '../../services/experimentService';

const ExperimentDetailView: React.FC = () => {
  const { experimentId } = useParams<{ experimentId: string }>();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: experiment, isLoading, error } = useQuery({
    queryKey: ['experimentV2', experimentId],
    queryFn: async () => {
      const response = await experimentService.getExperimentV2(Number(experimentId));
      return response.data.data;
    },
    enabled: !!experimentId,
  });

  const { data: results } = useQuery({
    queryKey: ['experimentResults', experimentId],
    queryFn: () => experimentService.getExperimentResults(Number(experimentId)),
    enabled: !!experimentId && experiment?.status === 'COMPLETED',
  });

  const { data: gate } = useQuery({
    queryKey: ['validationGate', experimentId],
    queryFn: () => experimentService.getValidationGate(Number(experimentId)),
    enabled: !!experimentId,
  });

  const handleStart = async () => {
    if (!experiment) return;
    try {
      await experimentService.startExperiment(experiment.id);
      // Refresh experiment
      window.location.reload();
    } catch (error) {
      console.error('Failed to start experiment:', error);
    }
  };

  const handleStop = async () => {
    if (!experiment) return;
    try {
      await experimentService.closeExperiment(experiment.id);
      // Refresh experiment
      window.location.reload();
    } catch (error) {
      console.error('Failed to stop experiment:', error);
    }
  };

  const handleDelete = async () => {
    if (!experiment) return;
    setIsDeleting(true);
    try {
      await experimentService.deleteExperiment(experiment.id);
      navigate('/experiments');
    } catch (error) {
      console.error('Failed to delete experiment:', error);
      setIsDeleting(false);
    }
  };

  const handleDuplicate = async () => {
    if (!experiment) return;
    try {
      const newExp = await experimentService.duplicateExperiment(experiment.id);
      navigate(`/experiments/${newExp.id}`);
    } catch (error) {
      console.error('Failed to duplicate experiment:', error);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Failed to load experiment" />;
  if (!experiment) return <ErrorMessage message="Experiment not found" />;

  const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
    DRAFT: 'default',
    PENDING: 'default',
    RUNNING: 'info',
    COMPLETED: 'success',
    CANCELLED: 'error',
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/experiments')} variant="text">
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {experiment.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Created {new Date(experiment.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {(experiment.status === 'DRAFT' || experiment.status === 'PENDING') && (
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={handleStart}
            >
              Start
            </Button>
          )}
          {experiment.status === 'RUNNING' && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Stop />}
              onClick={handleStop}
            >
              Close
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<FileCopy />}
            onClick={handleDuplicate}
          >
            Duplicate
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </Stack>
      </Box>

      {/* Status */}
      <Box sx={{ mb: 3 }}>
        <Chip
          label={experiment.status}
          color={statusColors[experiment.status]}
          variant="filled"
          size="medium"
        />
      </Box>

      {/* Validation Gate */}
      {gate && (
        <Box sx={{ mb: 3 }}>
          <ValidationGateAlert gate={gate} />
        </Box>
      )}

      {/* Main Details */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Experiment Details
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: 'textSecondary' }}>
                    Hypothesis
                  </Typography>
                  <Typography variant="body2">{experiment.hypothesis}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: 'textSecondary' }}>
                    Type
                  </Typography>
                  <Typography variant="body2">{experiment.type}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: 'textSecondary' }}>
                    Search Engines
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    {experiment.engines.map((engine: string) => (
                      <Chip key={engine} label={engine} size="small" />
                    ))}
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Timeline
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'textSecondary' }}>
                    Created
                  </Typography>
                  <Typography variant="caption">
                    {new Date(experiment.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                {experiment.startedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'textSecondary' }}>
                      Started
                    </Typography>
                    <Typography variant="caption">
                      {new Date(experiment.startedAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {experiment.completedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'textSecondary' }}>
                      Completed
                    </Typography>
                    <Typography variant="caption">
                      {new Date(experiment.completedAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Measurement Windows */}
      <Box sx={{ mb: 3 }}>
        <WindowsTimeline windows={experiment.windows} />
      </Box>

      {/* Results */}
      {experiment.status === 'COMPLETED' && results && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Results
          </Typography>
          <ExperimentResultsTable results={results} />
        </Box>
      )}

      {/* Pages Info */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Treatment Pages
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {experiment.treatmentPageIds.length} pages
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Control Pages
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {experiment.controlPageIds?.length || 0} pages
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Experiment?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{experiment.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ExperimentDetailView;
